import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import {
  DayOfWeek,
  AddressInput,
  Job,
  JobStatus,
  QuestionnaireQuestion,
  Site,
  UpdateJobInput,
} from '@core/generated/gql-types';
import { useJobStore } from '@core/job/useJobStore';
import { generateFriendlyAddress } from '@core/util/google-places.service';
import { useCoreLoopConfigurationStore } from '@core/coreLoopConfiguration/useCoreLoopConfigurationStore';
import { useClassificationStore } from '@core/classification/useClassificationStore';
import { useClientStore } from '@core/client/useClientStore';
import { useJobWorkingDays } from '@core/job/workingDays/useJobWorkingDays';
import {
  getJobSelectedWorkingDays,
  getSelectableWorkingDays,
} from '@core/job/workingDays/workingDays.util';
import ClientSiteSelector from '@/modules/jobs/components/create/ClientSiteSelector';
import JobDescription from '@/modules/jobs/components/create/JobDescription';
import QuestionnaireSection from '@/modules/jobs/components/create/QuestionnaireSection';
import WorkingDaysSelector from '@/modules/jobs/components/create/WorkingDaysSelector';
import SkillSetSelector from '@/modules/jobs/components/create/SkillSetSelector';
import SingleDatePicker from '@/components/shared/SingleDatePicker';
import { pluralize } from '@core/util/utils';
import usePostingQuestionnaire from '@/hooks/usePostingQuestionnaire';
import useGenerateAiJobDescription from '@/hooks/useGenerateAiJobDescription';
import {
  buildQuestionnaireCustomAttributes,
  buildResponsesFromCustomAttributes,
} from '@/utils/questionnaire.utils';
import { stripQuestionnaireDescriptionAddendum } from '@/utils/questionnaireDescription.util';
import {
  toDayjsFromDateAndTime,
  getJobEndTimeString,
  getJobStartTimeString,
  buildDaysInputForJobUpdate,
} from '@/utils/utils';
import Button from '@/components/Button';
import TimeSelectors from '@/modules/jobs/components/create/TimeSelectors';
import FilledJobScheduleConfirmModal from './FilledJobScheduleConfirmModal';

/** Matches backend `CoreLoopConfigurationClass.initialConfiguration.minimumHoursForShift`. */
const defaultMinimumHoursForShift = 3;

const addressInputFromSite = (site: Site | null): AddressInput | undefined => {
  if (!site?.address) {
    return undefined;
  }

  return {
    addressLine1: site.address.addressLine1 || '',
    addressLine2: site.address.addressLine2 || '',
    suburb: site.address.suburb || '',
    state: site.address.state || '',
    postcode: site.address.postcode || '',
    countryLabel: site.address.countryLabel || '',
    countryValue: site.address.countryValue || '',
    addressFriendly:
      site.address.addressFriendly || generateFriendlyAddress(site.address),
  };
};

const createJobEditValidationSchema = (
  minimumHoursForShift: number,
  options: { requireSite: boolean },
) => {
  const minimumDurationMessage = `Job duration must be at least ${minimumHoursForShift} ${pluralize('hour', minimumHoursForShift)}`;

  return Yup.object().shape({
    classification: Yup.object()
      .shape({
        id: Yup.string().required('Please select a job title'),
        name: Yup.string().required('Please select a job title'),
      })
      .required('Please select a job title'),
    siteId: options.requireSite
      ? Yup.string().required('Site is required')
      : Yup.string(),
    startDate: Yup.string().required('Start date is required'),
    endDate: Yup.string().required('End date is required'),
    startTime: Yup.string().required('Start time is required'),
    endTime: Yup.string()
      .required('End time is required')
      .when(['startTime', 'startDate', 'endDate'], (_, schema) => {
        return schema
          .test('is-after-start', 'End must be after start', function (value) {
            const { startDate, endDate, startTime } = this.parent;
            if (!startDate || !endDate || !startTime || !value) return true;
            const start = toDayjsFromDateAndTime(startDate, startTime);
            const end = toDayjsFromDateAndTime(endDate, value);
            if (!start || !end) return true;
            return end.isAfter(start);
          })
          .test(
            'minimum-shift-duration',
            minimumDurationMessage,
            function (value) {
              const { startDate, endDate, startTime } = this.parent;
              if (!startDate || !endDate || !startTime || !value) return true;
              const start = toDayjsFromDateAndTime(startDate, startTime);
              const end = toDayjsFromDateAndTime(endDate, value);
              if (!start || !end) return true;
              const durationHours = end.diff(start, 'hour', true);
              return durationHours >= minimumHoursForShift;
            },
          );
      }),
  });
};

type Props = {
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  job: Job;
  /** When false, site selector is hidden and site is not sent on save. */
  canEditSite?: boolean;
  onSuccess?: () => void;
};

const JobEditModal = ({
  isModalOpen,
  setIsModalOpen,
  job,
  canEditSite = false,
  onSuccess,
}: Props) => {
  const isFilledJob = job.itemStatus === JobStatus.Filled && !!job.userId;
  const showSiteSelector = canEditSite;

  const { updateJob } = useJobStore();
  const { coreLoopConfigurationItems, ensureCoreLoopConfiguration } =
    useCoreLoopConfigurationStore();
  const {
    classificationGroups: tenantClassificationGroups,
    fetchClassificationGroups,
    getClassificationGroupsByClassifications,
    isLoading: isClassificationLoading,
  } = useClassificationStore();
  const { clients, getClientById, fetchClientById } = useClientStore();
  const [classificationSearch, setClassificationSearch] = useState('');
  const [pendingFilledSubmission, setPendingFilledSubmission] = useState<{
    input: UpdateJobInput;
    siteLabel: string | null;
    addressLabel: string | null;
  } | null>(null);
  const [isConfirmingFilledSubmit, setIsConfirmingFilledSubmit] =
    useState(false);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);

  const minimumHoursForShift =
    coreLoopConfigurationItems?.minimumHoursForShift ??
    defaultMinimumHoursForShift;

  const showQuestionnaire = Boolean(
    coreLoopConfigurationItems?.tenantRequiresPostingQuestionnaire,
  );

  const {
    questions: questionnaireQuestions,
    responses: questionnaireResponses,
    updateResponse: updateQuestionnaireResponse,
    isLoading: isQuestionnaireLoading,
    isQuestionnaireComplete,
  } = usePostingQuestionnaire(
    showQuestionnaire,
    job.tenantId,
    job.customAttributes,
    job.id,
  );

  const validQuestionnaireQuestions = useMemo(
    () =>
      questionnaireQuestions.filter(
        (question): question is QuestionnaireQuestion => question !== null,
      ),
    [questionnaireQuestions],
  );

  const initialQuestionnaireResponses = useMemo(
    () =>
      buildResponsesFromCustomAttributes(
        validQuestionnaireQuestions,
        job.customAttributes,
      ),
    [job.customAttributes, validQuestionnaireQuestions],
  );

  const isQuestionnaireDirty = useMemo(() => {
    return (
      JSON.stringify(questionnaireResponses) !==
      JSON.stringify(initialQuestionnaireResponses)
    );
  }, [initialQuestionnaireResponses, questionnaireResponses]);

  useEffect(() => {
    if (!isModalOpen || !job.tenantId) return;
    ensureCoreLoopConfiguration(job.tenantId);
  }, [isModalOpen, job.tenantId, ensureCoreLoopConfiguration]);

  useEffect(() => {
    if (!isModalOpen || !job.tenantId) return;
    if (tenantClassificationGroups.length === 0) {
      fetchClassificationGroups(job.tenantId);
    }
  }, [
    isModalOpen,
    job.tenantId,
    tenantClassificationGroups.length,
    fetchClassificationGroups,
  ]);

  useEffect(() => {
    if (!isModalOpen || !job.clientId) return;
    const client = getClientById(job.clientId);
    if (!client) {
      fetchClientById(job.clientId);
    }
  }, [isModalOpen, job.clientId, getClientById, fetchClientById]);

  useEffect(() => {
    if (isModalOpen) {
      setClassificationSearch('');
      setSelectedSite(null);
    } else {
      setPendingFilledSubmission(null);
      setIsConfirmingFilledSubmit(false);
      setSelectedSite(null);
    }
  }, [isModalOpen]);

  const classificationGroups = useMemo(() => {
    const client = clients.find((c) => c.id === job.clientId);
    if (!client) return [];
    return getClassificationGroupsByClassifications(
      client.classifications || [],
    );
  }, [job.clientId, clients, getClassificationGroupsByClassifications]);

  const validationSchema = useMemo(
    () =>
      createJobEditValidationSchema(minimumHoursForShift, {
        requireSite: showSiteSelector,
      }),
    [minimumHoursForShift, showSiteSelector],
  );

  const initialStartTime = getJobStartTimeString(job);
  const initialEndTime = getJobEndTimeString(job);
  const initialStartDate = job.startDate
    ? dayjs(Number(job.startDate)).format('YYYY-MM-DD')
    : '';
  const initialEndDate = job.endDate
    ? dayjs(Number(job.endDate)).format('YYYY-MM-DD')
    : '';
  const initialWorkingDays = getJobSelectedWorkingDays(
    job,
    getSelectableWorkingDays(initialStartDate, initialEndDate),
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      classification: job.classification
        ? {
            id: job.classification.id,
            name: job.classification.name ?? '',
            description: job.classification.description ?? undefined,
            classificationGroupId:
              job.classification.classificationGroupId ?? '',
          }
        : undefined,
      siteId: job.siteId || '',
      description: stripQuestionnaireDescriptionAddendum(job.description),
      startDate: initialStartDate,
      endDate: initialEndDate,
      startTime: initialStartTime,
      endTime: initialEndTime,
      workingDays: initialWorkingDays as DayOfWeek[],
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, setStatus }) => {
      try {
        if (showQuestionnaire && !isQuestionnaireComplete()) {
          setStatus('Please complete all required questionnaire questions');
          return;
        }

        let startDateTime;
        if (values.startDate && values.startTime) {
          startDateTime = dayjs(
            `${values.startDate} ${values.startTime}`,
            'YYYY-MM-DD HH:mm',
            true,
          )
            .second(0)
            .millisecond(0);
        }

        let endDateTime;
        if (values.endDate && values.endTime) {
          endDateTime = dayjs(
            `${values.endDate} ${values.endTime}`,
            'YYYY-MM-DD HH:mm',
            true,
          )
            .second(0)
            .millisecond(0);
        } else if (values.startDate && values.endTime) {
          endDateTime = dayjs(
            `${values.startDate} ${values.endTime}`,
            'YYYY-MM-DD HH:mm',
            true,
          )
            .second(0)
            .millisecond(0);
        }

        if (!startDateTime || !endDateTime) {
          setStatus('Start and end date/time are required');
          return;
        }

        const startDate = String(startDateTime.valueOf());
        const endDate = String(endDateTime.valueOf());
        const days = buildDaysInputForJobUpdate(
          startDateTime,
          endDateTime,
          values.startTime,
          values.endTime,
          values.workingDays,
        );
        const siteChanged =
          showSiteSelector && values.siteId !== (job.siteId || '');
        const nextAddress = siteChanged
          ? addressInputFromSite(selectedSite)
          : undefined;
        const siteUpdate = siteChanged
          ? {
              siteId: values.siteId,
              ...(nextAddress ? { address: nextAddress } : {}),
            }
          : {};

        if (isFilledJob) {
          setPendingFilledSubmission({
            input: {
              startDate,
              endDate,
              days,
              description: values.description || undefined,
              ...siteUpdate,
              ...(showQuestionnaire &&
                isQuestionnaireDirty && {
                  customAttributes: buildQuestionnaireCustomAttributes(
                    validQuestionnaireQuestions,
                    questionnaireResponses,
                  ),
                }),
            },
            siteLabel: siteChanged ? (selectedSite?.name ?? null) : null,
            addressLabel: siteChanged
              ? (nextAddress?.addressFriendly ?? null)
              : null,
          });
          return;
        }

        const input: UpdateJobInput = {
          startDate,
          endDate,
          days,
          title: values.classification?.name ?? '',
          classificationId: values.classification?.id ?? '',
          description: values.description,
          ...siteUpdate,
          ...(showQuestionnaire &&
            isQuestionnaireDirty && {
              customAttributes: buildQuestionnaireCustomAttributes(
                validQuestionnaireQuestions,
                questionnaireResponses,
              ),
            }),
        };
        const updated = await updateJob(job.id, input);

        if (!updated) {
          toast.error('Failed to save changes. Please try again.');
          return;
        }
        setIsModalOpen(false);
        onSuccess?.();
        toast.success('Updates saved successfully');
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const setFieldValueRef = useRef(formik.setFieldValue);
  setFieldValueRef.current = formik.setFieldValue;

  const {
    selectableDays: selectableWorkingDays,
    isVisible: showWorkingDays,
    toggleDay: toggleWorkingDay,
    workingDaysSummary,
    daysOffSummary,
    preserveSelectionForSchedule,
  } = useJobWorkingDays({
    startDate: formik.values.startDate,
    endDate: formik.values.endDate,
    selectedDays: formik.values.workingDays,
    onSelectedDaysChange: (days) =>
      setFieldValueRef.current('workingDays', days),
    numberOfWorkers: 1,
  });

  useLayoutEffect(() => {
    if (!isModalOpen) return;
    preserveSelectionForSchedule(initialStartDate, initialEndDate);
    formik.setFieldValue('workingDays', initialWorkingDays);
    // Seed before paint so useJobWorkingDays auto-sync cannot wipe the job's days.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional open-seed
  }, [isModalOpen, job.id]);

  const submitPendingFilledChanges = async () => {
    if (!pendingFilledSubmission) return;

    setIsConfirmingFilledSubmit(true);

    try {
      const updated = await updateJob(job.id, pendingFilledSubmission.input);

      if (!updated) {
        toast.error('Failed to save changes. Please try again.');
        return;
      }

      setPendingFilledSubmission(null);
      setIsModalOpen(false);
      onSuccess?.();
      toast.success('Updates saved successfully');
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'An unexpected error occurred',
      );
    } finally {
      setIsConfirmingFilledSubmit(false);
    }
  };

  const cancelFilledConfirmation = () => {
    setPendingFilledSubmission(null);
  };

  const { generateAiJobDescription, isGeneratingAi } =
    useGenerateAiJobDescription({
      descriptionField: 'description',
      setFieldError: formik.setFieldError,
      setFieldValue: formik.setFieldValue,
      getInput: () => {
        const client = getClientById(job.clientId);
        return {
          classificationName: formik.values.classification?.name,
          startTime: formik.values.startTime,
          clientName: client?.name,
          siteAddress: selectedSite?.address || job.address,
          siteLabel: selectedSite?.name || job.site?.name,
        };
      },
    });

  if (!isModalOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-8 sm:px-6">
        <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
          <div className="flex flex-shrink-0 items-center justify-between px-8 pt-8 pb-6">
            <h3 className="text-2xl font-bold text-gray-800">Edit Job</h3>
            <Button
              variant="text"
              onClick={() => setIsModalOpen(false)}
              className="min-w-0 px-2 py-2 font-normal"
            >
              <FontAwesomeIcon icon={faXmark} className="h-10 w-10" />
            </Button>
          </div>

          <form
            onSubmit={formik.handleSubmit}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="flex-1 space-y-6 overflow-y-auto px-8 pb-6">
              {isFilledJob && (
                <div className="rounded-md border border-red-200 bg-red-50 p-4">
                  <p className="text-sm text-red-700">
                    This job is already filled. You can update its schedule
                    (dates, working days, and times), description, and site.
                    Changing the site also updates the job address. Title and
                    classification are locked.
                  </p>
                </div>
              )}

              {formik.status && (
                <div className="rounded-md border border-red-200 bg-red-50 p-4">
                  <p className="text-sm text-red-600">{formik.status}</p>
                </div>
              )}

              {!isFilledJob && (
                <SkillSetSelector
                  value={formik.values.classification || {}}
                  onChange={(value) =>
                    formik.setFieldValue('classification', value)
                  }
                  classificationGroups={classificationGroups}
                  isLoading={isClassificationLoading}
                  searchValue={classificationSearch}
                  onSearchChange={setClassificationSearch}
                  error={formik.errors.classification}
                  touched={!!formik.touched.classification}
                />
              )}

              {showSiteSelector && (
                <ClientSiteSelector
                  clientId={job.clientId}
                  value={formik.values.siteId}
                  onChange={(siteId) => {
                    formik.setFieldValue('siteId', siteId);
                    formik.setFieldTouched('siteId', true, false);
                  }}
                  onSiteSelected={setSelectedSite}
                  error={
                    typeof formik.errors.siteId === 'string'
                      ? formik.errors.siteId
                      : undefined
                  }
                  touched={formik.touched.siteId}
                />
              )}

              <JobDescription
                value={formik.values.description}
                onChange={(value) => formik.setFieldValue('description', value)}
                onBlur={() => formik.setFieldTouched('description', true)}
                onGenerateAi={generateAiJobDescription}
                isGeneratingAi={isGeneratingAi}
                error={
                  typeof formik.errors.description === 'string'
                    ? formik.errors.description
                    : undefined
                }
                touched={!!formik.touched.description}
              />

              {showQuestionnaire && (
                <QuestionnaireSection
                  questions={questionnaireQuestions}
                  responses={questionnaireResponses}
                  updateResponse={updateQuestionnaireResponse}
                  isLoading={isQuestionnaireLoading}
                />
              )}

              <div className="grid grid-cols-2 gap-4">
                <TimeSelectors
                  time={formik.values.startTime || ''}
                  onTimeChange={(val) => formik.setFieldValue('startTime', val)}
                  timeError={formik.errors.startTime || ''}
                  label="Start time"
                  formikId="startTime"
                  handleBlur={formik.handleBlur}
                  touched={!!formik.touched.startTime}
                />

                <div>
                  <label
                    className="mb-1 block text-sm font-medium text-gray-700"
                    htmlFor="startDate"
                  >
                    Start date
                  </label>
                  <SingleDatePicker
                    value={
                      formik.values.startDate
                        ? dayjs(formik.values.startDate).toDate()
                        : new Date()
                    }
                    onChange={(date) => {
                      formik.setFieldValue(
                        'startDate',
                        dayjs(date).format('YYYY-MM-DD'),
                      );
                      formik.setFieldTouched('startDate', true, false);
                    }}
                    panelAlignment="left"
                    isInvalid={
                      !!(formik.touched.startDate && formik.errors.startDate)
                    }
                    className="w-full rounded-md px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
                  />
                  {formik.touched.startDate && formik.errors.startDate && (
                    <div className="mt-1 text-sm text-red-600">
                      {formik.errors.startDate}
                    </div>
                  )}
                </div>

                <TimeSelectors
                  time={formik.values.endTime || ''}
                  onTimeChange={(val) => formik.setFieldValue('endTime', val)}
                  timeError={formik.errors.endTime || ''}
                  label="End time"
                  formikId="endTime"
                  handleBlur={formik.handleBlur}
                  touched={formik.dirty}
                />

                <div>
                  <label
                    className="mb-1 block text-sm font-medium text-gray-700"
                    htmlFor="endDate"
                  >
                    End date
                  </label>
                  <SingleDatePicker
                    value={
                      formik.values.endDate
                        ? dayjs(formik.values.endDate).toDate()
                        : new Date()
                    }
                    onChange={(date) => {
                      formik.setFieldValue(
                        'endDate',
                        dayjs(date).format('YYYY-MM-DD'),
                      );
                      formik.setFieldTouched('endDate', true, false);
                    }}
                    panelAlignment="left"
                    isInvalid={
                      !!(formik.touched.endDate && formik.errors.endDate)
                    }
                    className="w-full rounded-md px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
                  />
                  {formik.touched.endDate && formik.errors.endDate && (
                    <div className="mt-1 text-sm text-red-600">
                      {formik.errors.endDate}
                    </div>
                  )}
                </div>
              </div>

              {showWorkingDays && (
                <div className="space-y-2">
                  <WorkingDaysSelector
                    selectableDays={selectableWorkingDays}
                    selectedDays={formik.values.workingDays}
                    onToggleDay={toggleWorkingDay}
                  />
                  <p className="text-sm text-gray-500">
                    {`Working days: ${workingDaysSummary}.${daysOffSummary}`}
                  </p>
                </div>
              )}
            </div>

            <div className="flex flex-shrink-0 items-end justify-end gap-4 border-t px-8 py-4">
              <Button
                variant="outline"
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="py-2 text-sm"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={
                  formik.isSubmitting ||
                  (!formik.dirty && !isQuestionnaireDirty) ||
                  !formik.isValid ||
                  (showQuestionnaire && !isQuestionnaireComplete())
                }
                className="py-2 text-sm"
              >
                {formik.isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <FilledJobScheduleConfirmModal
        isOpen={!!pendingFilledSubmission}
        onClose={cancelFilledConfirmation}
        onConfirm={submitPendingFilledChanges}
        startDate={pendingFilledSubmission?.input.startDate ?? null}
        endDate={pendingFilledSubmission?.input.endDate ?? null}
        workingDays={pendingFilledSubmission?.input.days?.days}
        siteLabel={pendingFilledSubmission?.siteLabel}
        addressLabel={pendingFilledSubmission?.addressLabel}
        isSubmitting={isConfirmingFilledSubmit}
      />
    </>
  );
};

export default JobEditModal;
